<script>
// composables
import { useAI } from 'dashboard/composables/useAI';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    selectedInbox: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  setup() {
    const { isAIIntegrationEnabled, getApiKey } = useAI();
    const apiKey = getApiKey();
    const brandName = window.globalConfig?.BRAND_NAME || '';

    return { isAIIntegrationEnabled, apiKey, brandName };
  },
  data() {
    return {
      isLoading: false,
      selectedBotId: null,
      currentInboxBotId: null,
      showSelectionModal: false,
      selectedOption: null,
      showOpenAISetupPrompt: false,
      showDisconnectConfirmation: false,
      pendingConversationsCount: 0,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      agentBots: 'agentBots/getBots',
    }),
    header() {
      return this.$t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.TITLE', {
        inboxName: this.selectedInbox.name || '',
      });
    },
    hasSelectedBot() {
      return !!this.selectedBotId;
    },
    isAgentBotsFeatureEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.AGENT_BOTS
      );
    },
    isDefaultAgentBotFeatureEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.DEFAULT_AGENT_BOT_API_KEY
      );
    },
  },
  mounted() {
    // Fetch all agent bots when modal opens
    this.$store.dispatch('agentBots/get');

    // Get the current agent bot for this inbox if any
    if (this.selectedInbox.id) {
      this.fetchCurrentAgentBot();
    }
  },
  methods: {
    handleBotClick(botId) {
      // Only allow selection if no bot is currently enabled
      // or if clicking on the currently enabled bot
      if (!this.currentInboxBotId || this.currentInboxBotId === botId) {
        this.selectBot(botId);
      }
    },
    async fetchCurrentAgentBot() {
      try {
        this.isLoading = true;
        const response = await this.$store.dispatch(
          'inboxes/getAgentBot',
          this.selectedInbox.id
        );

        if (response && response.agent_bot) {
          this.currentInboxBotId = response.agent_bot.id;
          this.selectedBotId = response.agent_bot.id;
        } else {
          this.currentInboxBotId = null;
          this.selectedBotId = null;
        }
      } catch (error) {
        this.currentInboxBotId = null;
      } finally {
        this.isLoading = false;
      }
    },
    closeModal() {
      this.$emit('close');
    },
    selectBot(botId) {
      this.selectedBotId = botId;
    },
    async applyBot() {
      this.showSelectionModal = true;
    },

    async handleSelection(option) {
      this.selectedOption = option;
      this.showSelectionModal = false;

      if (!this.selectedBotId) {
        return;
      }

      try {
        this.isLoading = true;

        // Set the selected bot for the inbox
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.selectedInbox.id,
          botId: this.selectedBotId,
          botType: option === 'openai' ? 'openai' : 'default',
        });

        useAlert(
          this.$t(
            'INTEGRATION_SETTINGS.AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'
          )
        );
        this.closeModal();
      } catch (error) {
        useAlert(
          this.$t(
            'INTEGRATION_SETTINGS.AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'
          )
        );
      } finally {
        this.isLoading = false;
      }
    },
    async fetchPendingConversations() {
      try {
        // Make direct API call to get pending, unassigned conversations for this inbox
        const response = await ConversationApi.get({
          inboxId: this.selectedInbox.id,
          status: 'pending',
          assigneeType: 'unassigned',
          page: 1,
        });

        const pendingConversations = response.data?.data?.payload || [];

        this.pendingConversationsCount = pendingConversations.length;
      } catch (error) {
        this.pendingConversationsCount = 0;
      }
    },
    async removeBot() {
      // First, get the count of pending conversations
      await this.fetchPendingConversations();

      // Show confirmation dialog
      this.showDisconnectConfirmation = true;
    },
    async confirmRemoveBot() {
      try {
        this.isLoading = true;
        this.showDisconnectConfirmation = false;

        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.selectedInbox.id,
        });

        useAlert(
          this.$t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.DISCONNECT_SUCCESS')
        );
        this.closeModal();
      } catch (error) {
        useAlert(this.$t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
    handleOpenAISelection() {
      if (!this.isAIIntegrationEnabled || !this.apiKey) {
        this.handleSelection('openai');
      } else {
        this.handleSelection('openai');
      }
    },
    goToOpenAISetup() {
      this.showOpenAISetupPrompt = false;
      // Use the correct route for OpenAI integration
      this.$router.push(
        `/app/accounts/${this.accountId}/settings/integrations/openai`
      );
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="closeModal">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="header" />

      <div class="p-4">
        <div v-if="isLoading" class="text-center p-4">
          <p class="mt-2">
            {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.LOADING') }}
          </p>
        </div>

        <div
          v-else-if="!agentBots.length"
          class="text-center p-4 text-slate-600"
        >
          {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.NO_BOTS') }}
        </div>

        <div v-else class="space-y-4">
          <p class="text-sm mb-2">
            {{
              currentInboxBotId
                ? $t(
                    'INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.CURRENTLY_ENABLED_BOT'
                  )
                : $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.SELECT_PROMPT')
            }}
          </p>

          <div
            v-for="bot in agentBots"
            :key="bot.id"
            class="flex items-center p-3 border rounded-lg transition-all duration-150"
            :class="{
              'border-woot-500 bg-blue-50': selectedBotId === bot.id,
              'border-green-500 ': currentInboxBotId === bot.id,
              'hover:bg-blue-50 hover:border-blue-200':
                !currentInboxBotId && selectedBotId !== bot.id,
              'cursor-pointer':
                !currentInboxBotId || currentInboxBotId === bot.id,
              'opacity-50 cursor-not-allowed':
                currentInboxBotId && currentInboxBotId !== bot.id,
            }"
            @click="handleBotClick(bot.id)"
          >
            <div
              class="w-8 h-8 rounded-full overflow-hidden mr-3 flex-shrink-0"
            >
              <img
                v-if="bot.avatar_url"
                :src="bot.avatar_url"
                :alt="bot.name"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full bg-woot-100 flex items-center justify-center text-woot-500"
              >
                <span class="text-xs font-medium">{{
                  bot.name.charAt(0).toUpperCase()
                }}</span>
              </div>
            </div>

            <div class="flex-1 min-w-0">
              <div class="flex items-center">
                <p
                  class="font-medium text-sm text-slate-900 dark:text-white truncate"
                >
                  {{ bot.name }}
                </p>
                <span
                  v-if="currentInboxBotId === bot.id"
                  class="ml-2 px-1.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 text-[10px] flex-shrink-0"
                >
                  {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.ENABLED') }}
                </span>
              </div>
              <p
                class="text-m text-slate-800 dark:text-slate-200 mt-0.5 line-clamp-1"
              >
                {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.DESCRIPTION') }}:{{
                  bot.description
                }}
              </p>
              <p
                v-if="bot.outgoing_url"
                class="mt-0.5 text-slate-700 dark:text-slate-300 truncate"
              >
                <span class="font-medium">
                  {{
                    $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.OUTGOING_URL')
                  }}:
                </span>
                {{ bot.outgoing_url }}
              </p>
            </div>
          </div>

          <div class="flex justify-between pt-4 border-t mt-4">
            <!-- Show disconnect button only when a bot is enabled -->
            <woot-button
              v-if="currentInboxBotId"
              variant="clear"
              color-scheme="alert"
              :is-loading="isLoading"
              @click="removeBot"
            >
              {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.DISCONNECT') }}
            </woot-button>

            <div class="flex gap-2 ml-auto">
              <woot-button variant="clear" @click="closeModal">
                {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.CANCEL') }}
              </woot-button>

              <!-- Only show Apply button when no bot is enabled yet -->
              <woot-button
                v-if="!currentInboxBotId"
                :is-disabled="!hasSelectedBot"
                :is-loading="isLoading"
                @click="applyBot"
              >
                {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.APPLY') }}
              </woot-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </woot-modal>

  <!-- Selection Modal -->
  <woot-modal
    :show="showSelectionModal"
    :on-close="() => (showSelectionModal = false)"
  >
    <div class="p-4">
      <h3 class="text-lg font-medium mb-4">
        {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.CHOOSE_OPTION') }}
      </h3>
      <div class="flex flex-col space-y-4">
        <woot-button
          v-if="isDefaultAgentBotFeatureEnabled"
          @click="handleSelection('default')"
        >
          {{
            $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.DEFAULT_BOT', {
              brandName,
            })
          }}
        </woot-button>
        <woot-button @click="handleSelection('openai')">
          {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.OPENAI') }}
        </woot-button>
      </div>
    </div>
  </woot-modal>

  <!-- OpenAI Setup Prompt Modal -->
  <woot-modal
    :show="showOpenAISetupPrompt"
    :on-close="() => (showOpenAISetupPrompt = false)"
  >
    <div class="p-4">
      <h3 class="text-lg font-medium mb-4">
        {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.OPENAI_SETUP_REQUIRED') }}
      </h3>
      <p class="mb-4">
        {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.OPENAI_SETUP_MESSAGE') }}
      </p>
      <div class="flex justify-end space-x-2">
        <woot-button
          variant="clear"
          @click="() => (showOpenAISetupPrompt = false)"
        >
          {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.CANCEL') }}
        </woot-button>
        <woot-button @click="goToOpenAISetup">
          {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.SETUP_OPENAI') }}
        </woot-button>
      </div>
    </div>
  </woot-modal>

  <!-- Disconnect Confirmation Modal -->
  <woot-modal
    :show="showDisconnectConfirmation"
    :on-close="() => (showDisconnectConfirmation = false)"
  >
    <div class="p-4">
      <h3 class="text-lg font-medium mb-4">
        {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.DISCONNECT_TITLE') }}
      </h3>
      <p class="mb-4">
        <span v-if="pendingConversationsCount > 0">
          {{
            $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.DISCONNECT_WARNING', {
              count: pendingConversationsCount,
            })
          }}
        </span>
        <span v-else>
          {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.DISCONNECT_MESSAGE') }}
        </span>
      </p>
      <div class="flex justify-end space-x-2">
        <woot-button
          variant="clear"
          @click="() => (showDisconnectConfirmation = false)"
        >
          {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.CANCEL') }}
        </woot-button>
        <woot-button
          color-scheme="alert"
          :is-loading="isLoading"
          @click="confirmRemoveBot"
        >
          {{ $t('INTEGRATION_SETTINGS.AGENT_BOTS.MODAL.DISCONNECT_CONFIRM') }}
        </woot-button>
      </div>
    </div>
  </woot-modal>
</template>
